import Cookies from 'cookies';
import {
  type GetServerSidePropsContext,
  type GetServerSidePropsResult
} from 'next';
import HeadTags from '@/sem/HeadTags';
import SemContainer from '@/sem/SemContainer';
import { semProject, semTrack } from '~/constants/eventable';
import SemProviders, { SemProvidersProps } from '~/context/sem/SemProviders';
import { OwnProps as AppProps } from '~/pages/_app';
import getFeatureBranchRedirect from '~/server-props/common/getFeatureBranchRedirect';
import setDatadogUser from '~/server-props/common/setDatadogUser';
import { isNotFound, isRedirect } from '~/server-props/common/utils/guards';
import getProps from '~/server-props/sem/getProps';
import getRequestContext from '~/server-props/sem/utils/getRequestContext';
import { ErrorBoundary } from '~/utilities/ErrorBoundary';

export interface OwnProps {
  forProviders: SemProvidersProps;
}

export default function SemModularPage(props: OwnProps) {
  return (
    <ErrorBoundary>
      <SemProviders
        eventableProject={semProject}
        eventableTrack={semTrack}
        {...props.forProviders}
      >
        <HeadTags />
        <SemContainer />
      </SemProviders>
    </ErrorBoundary>
  );
}

export async function getServerSideProps(
  context: GetServerSidePropsContext
): Promise<
  GetServerSidePropsResult<AppProps & { forProviders: SemProvidersProps }>
> {
  if (context.resolvedUrl.includes('.js.map')) {
    return { notFound: true };
  }

  const requestContext = getRequestContext(context.req);
  const serverCookies = new Cookies(context.req, context.res, { secure: true });

  const redirect = getFeatureBranchRedirect(
    serverCookies,
    context.query,
    context.resolvedUrl,
    requestContext.kubernetesAppName
  );

  if (redirect) {
    return { redirect };
  }

  setDatadogUser(context, serverCookies);
  const result = await getProps(context, requestContext, serverCookies);

  if (isNotFound(result) || isRedirect(result)) {
    return result;
  }

  const { forApp, forProviders } = await result.props;

  return { props: { ...forApp, forProviders } };
}
